import { supabase } from '@/integrations/supabase/client';

// Client side of the whole-folder document import. The run-document-import
// edge function is time-boxed and resumable (it groups the tenant's pending
// documents into project dossiers and reads each in one LLM call), so the
// browser simply calls it again until it reports done.

interface ImportRunResponse {
  dossiersTotal: number;
  dossiersRemaining: number;
  filesTotal: number;
  filesRemaining: number;
  projectsWritten: number;
  failed: number;
  progressed: number;
  /** Loose files the whole-folder pass sorted into a project in this call; part of `progressed`. */
  filesGrouped?: number;
  /** False when this call wrote candidates but had no time left to rebuild the proposals. */
  consolidated: boolean;
  throttled: boolean;
  retryAfterSeconds: number;
  done: boolean;
}

export interface DocumentImportProgress {
  /** Project folders (dossiers) plus loose files waiting when the run started. */
  total: number;
  processed: number;
  /** Project folders among `total`. */
  projectsTotal: number;
  projectsWritten: number;
  /** The AI provider asked us to slow down; the run is pausing, not stuck. */
  waiting: boolean;
}

export interface DocumentImportSummary extends DocumentImportProgress {
  /** Left unprocessed: failing items, or a provider that stayed rate-limited. */
  remaining: number;
  failed: number;
  /** Nothing was waiting to be analysed. */
  nothingToDo: boolean;
}

const MAX_CALLS = 300;
// Consecutive rate-limited calls without any progress before giving up for now.
const MAX_IDLE_WAITS = 8;
const MIN_WAIT_SECONDS = 5;
const MAX_WAIT_SECONDS = 60;

const sleep = (seconds: number) => new Promise((resolve) => setTimeout(resolve, seconds * 1000));

/**
 * Analyses every pending document of the caller's tenant. Throws only if the
 * very first call fails; after that it returns what it managed, since the
 * rest is picked up by the next run or the scheduled sync.
 */
export async function runDocumentImport(
  onProgress?: (progress: DocumentImportProgress) => void,
): Promise<DocumentImportSummary> {
  const state: DocumentImportProgress = { total: 0, processed: 0, projectsTotal: 0, projectsWritten: 0, waiting: false };
  let remaining = 0;
  let failed = 0;
  let idleWaits = 0;
  let needsConsolidation = false;

  for (let call = 0; call < MAX_CALLS; call++) {
    const { data, error } = await supabase.functions.invoke('run-document-import', { body: {} });
    if (error || !data || typeof (data as ImportRunResponse).progressed !== 'number') {
      if (call === 0) throw error ?? new Error('Document import did not respond');
      console.error('run-document-import failed mid-run:', error);
      break;
    }
    const res = data as ImportRunResponse;
    const finished = res.progressed - (res.filesGrouped ?? 0);
    state.processed += finished;
    if (finished > 0) needsConsolidation = !res.consolidated;
    state.projectsWritten += res.projectsWritten;
    remaining = res.dossiersRemaining + res.filesRemaining;
    // Sorting loose files into projects changes how many items there are, so the total follows the latest plan.
    if (call === 0) state.projectsTotal = res.dossiersTotal;
    state.total = Math.max(state.total, state.processed + remaining);
    failed = res.failed;

    if (res.done) {
      state.waiting = false;
      onProgress?.({ ...state });
      break;
    }
    if (res.throttled) {
      idleWaits = res.progressed > 0 ? 0 : idleWaits + 1;
      if (idleWaits > MAX_IDLE_WAITS) break;
      state.waiting = true;
      onProgress?.({ ...state });
      await sleep(Math.min(MAX_WAIT_SECONDS, Math.max(MIN_WAIT_SECONDS, res.retryAfterSeconds || 0)));
      continue;
    }
    // Not rate-limited and nothing moved: what is left keeps failing. Stop rather than spin.
    if (res.progressed === 0) break;
    idleWaits = 0;
    state.waiting = false;
    onProgress?.({ ...state });
  }

  if (needsConsolidation) {
    // Best-effort: on failure the proposals are rebuilt by the next run or review instead.
    const { error } = await supabase.functions.invoke('consolidate-candidates', { body: {} });
    if (error) console.error('consolidate-candidates failed:', error);
  }

  if (state.processed > 0) {
    // Registers that were already started were refreshed by the import; rescan so signals follow. Best-effort.
    const { error } = await supabase.functions.invoke('generate-signals', { body: { action: 'scan' } });
    if (error) console.error('generate-signals scan after import failed:', error);
  }

  return { ...state, waiting: false, remaining, failed, nothingToDo: state.total === 0 };
}
